import { toastError } from 'admination-react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
} from 'react'
import type { ModalSource as DsModalSource } from 'admination-react'
import { listDashboard, loadDashboardCoaching } from '@/core/data/coachings'
import { startLiveConnection, type LiveHint } from '@/core/live/connection'
import { ApiError, captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { useAuth } from './auth'
import type { Pending, RowsEdit } from '@/core/lib/optimistic'
import { listMembers, type Member } from '@/core/data/members'
import { me, subscribeMe, type Me } from '@/core/data/session'
import { hatRueckzug, type DashboardCoaching, type StatusFilter, type ZurueckgezogenCoaching } from '@/core/types'
import type { UnterschriftDokument } from '@/variants'
import type { FormValues } from '@/core/components/form/formModel'
import { EMPTY_COACHING_VIEW_FILTER, type CoachingViewFilter } from '@/core/lib/coachingFilter'
import {
  EMPTY_MEMBER_VIEW_FILTER,
  type MemberViewFilter,
} from '@/core/components/orgsettings/memberViewConfig'
import {
  EMPTY_MASSNAHME_VIEW_FILTER,
  type MassnahmeViewFilter,
} from '@/core/components/orgsettings/massnahmeViewConfig'

export type Modal =
  | 'erstgespraech'
  | 'avgs'
  | 'bewilligung'
  | 'vertrag'
  | 'personal'
  | 'invite-coach'
  | 'member'
  | 'ecn'
  | 'abschluss'
  | 'create-massnahme'
  | 'massnahme'
  | 'modul'
  | 'create-modul'
  | 'verbis-lebenslauf'
  | 'aufnahme-potenzialanalyse'
  | 'kompetenzfeststellung'
  | 'kompetenzbilanzierung'
  | 'teilnehmende-person-anlegen'
  | 'dokument'
  | 'dokument-hochladen'

export type PendingErstgespraech = {
  values: FormValues
  coachingId: string
  teilnehmendeId: string
}

export type ModalSource = DsModalSource | null

export type AppView =
  | 'dashboard'
  | 'coaches'
  | 'massnahmen'
  | 'module'
  | 'anwesenheit'

export type OrgSettingsTab = 'organisation' | 'anleitung'

export type CoachingModal = Exclude<Modal, 'invite-coach' | 'member' | 'create-massnahme' | 'massnahme' | 'modul' | 'create-modul'>

type ScreenPayload =
  | { kind: Exclude<Modal, 'member' | 'massnahme' | 'modul'> }
  | { kind: 'member'; memberId: string | null }
  | { kind: 'massnahme'; massnahmeId: string | null }
  | { kind: 'modul'; massnahmeId: string | null; modulId: string | null }

export type ScreenDocument = { name: string | null; id: string | null }
type ScreenContext = {
  tab: string
  coachingId: string | null
  participantId: string | null
  source: ModalSource
  document: ScreenDocument
  returnTo?: 'documents'
  kalenderAnker?: string
}
export type PhaseScreen = ScreenPayload & ScreenContext
export type StaffParticipant = { id: string; name: string }
export type ParticipantScreen =
  | { kind: 'akte'; participant: StaffParticipant; source?: DsModalSource }
  | {
      kind: 'dokumentUnterschrift'
      dokument: UnterschriftDokument
      participant: StaffParticipant
      source?: DsModalSource
      ansehen?: boolean
    }
export type Screen = PhaseScreen | (ParticipantScreen & { tab: null; coachingId: null; participantId: null; document: ScreenDocument; returnTo?: never; kalenderAnker?: never })
type HistoryFrame = {
  screen: PhaseScreen
  selectedCoachingId: string | null
  selectedCoachingDocName: string | null
  selectedCoachingDocId: string | null
}

type ViewControls = {
  workspaceSwitching: boolean
  selectedCoachingId: string | null
  orgSettingsTab: OrgSettingsTab
  coachingFilter: StatusFilter | null
  coachingSort: { key: string; dir: 'asc' | 'desc' } | null
  coachingSearch: string
  dashboardHiddenStages: StatusFilter[]
  selectedMemberUserId: string | null
  memberSort: { key: string; dir: 'asc' | 'desc' } | null
  memberSearch: string
  selectedMassnahmeId: string | null
  selectedModulId: string | null
  massnahmeSort: { key: string; dir: 'asc' | 'desc' } | null
  massnahmeSearch: string
  moduleSort: { key: string; dir: 'asc' | 'desc' } | null
  moduleSearch: string
  moduleViewFilter: { usage: string[] }
}

export type AppState = ViewControls & {
  screen: Screen | null
  history: HistoryFrame[]
  pendingErstgespraech: PendingErstgespraech | null
  tbGenerating: string[]
  dashboardVersion: number
  selectedCoachingDocName: string | null
  selectedCoachingDocId: string | null
  view: AppView
  settings: 'organisation' | 'personal' | null
  coachingViewFilter: CoachingViewFilter
  membersVersion: number
  memberViewFilter: MemberViewFilter
  massnahmenVersion: number
  massnahmeViewFilter: MassnahmeViewFilter
}

type ControlAction = {
  [Key in keyof ViewControls]: { type: 'SET_CONTROL'; key: Key; value: ViewControls[Key] }
}[keyof ViewControls]

export type Action =
  | ControlAction
  | { type: 'OPEN_ERSTGESPRAECH'; source: ModalSource }
  | {
      type: 'OPEN_SCREEN'
      screen: ScreenPayload & {
        tab: string
        coachingId: string | null
        source?: ModalSource
        participantId?: string
        document?: ScreenDocument
        returnTo?: 'documents'
        kalenderAnker?: string
      }
      remember?: boolean
    }
  | { type: 'OPEN_PARTICIPANT'; screen: ParticipantScreen }
  | { type: 'SET_PERSON'; coachingId: string; participantId: string }
  | { type: 'CHANGE_TAB'; tab: string }
  | { type: 'CHANGE_PHASE'; target: { modal: CoachingModal; tab: string; document?: ScreenDocument; returnTo?: 'documents' }; remember?: boolean }
  | { type: 'GO_BACK' }
  | { type: 'SET_PENDING_ERSTGESPRAECH'; pending: PendingErstgespraech }
  | {
      type: 'REOPEN_ERSTGESPRAECH'
      coachingId: string | null
      pending: PendingErstgespraech
      source: ModalSource | null
    }
  | { type: 'TB_GENERATING'; coachingId: string; generating: boolean }
  | { type: 'CLOSE' }
  | { type: 'DISMISS' }
  | { type: 'CLOSE_FOR'; coachingId: string }
  | { type: 'BUMP_DASHBOARD' }
  | { type: 'SELECT_COACHING_DOC'; name: string | null; id?: string | null }
  | { type: 'SET_VIEW'; view: AppView }
  | { type: 'SET_SETTINGS'; settings: NonNullable<AppState['settings']> }
  | { type: 'SET_COACHING_VIEW_FILTER'; filter: Partial<CoachingViewFilter> }
  | { type: 'BUMP_MEMBERS' }
  | { type: 'SET_MEMBER_VIEW_FILTER'; filter: Partial<MemberViewFilter> }
  | { type: 'BUMP_MASSNAHMEN' }
  | { type: 'SET_MASSNAHME_VIEW_FILTER'; filter: Partial<MassnahmeViewFilter> }

export const initialStaffState: AppState = {
  workspaceSwitching: false,
  screen: null,
  history: [],
  pendingErstgespraech: null,
  tbGenerating: [],
  dashboardVersion: 0,
  selectedCoachingId: null,
  selectedCoachingDocName: null,
  selectedCoachingDocId: null,
  view: 'dashboard',
  settings: null,
  orgSettingsTab: 'organisation',
  coachingFilter: null,
  coachingSort: { key: 'name', dir: 'asc' },
  coachingSearch: '',
  dashboardHiddenStages: ['abgeschlossen', 'teilnahmeAbgeschlossen'],
  coachingViewFilter: EMPTY_COACHING_VIEW_FILTER,
  selectedMemberUserId: null,
  membersVersion: 0,
  memberSort: { key: 'name', dir: 'asc' },
  memberSearch: '',
  memberViewFilter: EMPTY_MEMBER_VIEW_FILTER,
  selectedMassnahmeId: null,
  massnahmenVersion: 0,
  selectedModulId: null,
  massnahmeSort: { key: 'bezeichnung', dir: 'asc' },
  massnahmeSearch: '',
  massnahmeViewFilter: EMPTY_MASSNAHME_VIEW_FILTER,
  moduleSort: { key: 'name', dir: 'asc' },
  moduleSearch: '',
  moduleViewFilter: { usage: [] },
}

export function reduceStaff(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_CONTROL': {
      let screen = state.screen
      if (screen?.kind === 'member' && action.key === 'selectedMemberUserId') screen = { ...screen, memberId: action.value }
      if ((screen?.kind === 'massnahme' || screen?.kind === 'modul') && action.key === 'selectedMassnahmeId') screen = { ...screen, massnahmeId: action.value }
      if (screen?.kind === 'modul' && action.key === 'selectedModulId') screen = { ...screen, modulId: action.value }
      if (screen && action.key === 'selectedCoachingId' && action.value !== screen.coachingId) screen = { ...screen, document: { name: null, id: null } }
      return { ...state, screen, [action.key]: action.value }
    }
    case 'OPEN_ERSTGESPRAECH':
      return {
        ...state,
        screen: { kind: 'erstgespraech', tab: 'checkliste', coachingId: null, participantId: null,
          source: action.source, document: { name: null, id: null } },
        pendingErstgespraech: null,
        history: [],
      }
    case 'OPEN_PARTICIPANT':
      return { ...closedState(state), screen: { ...action.screen, tab: null, coachingId: null, participantId: null, document: { name: null, id: null } } }
    case 'OPEN_SCREEN': {
      const screen = {
        ...action.screen,
        document: action.screen.document ?? (action.screen.coachingId === state.selectedCoachingId ? { name: state.selectedCoachingDocName, id: state.selectedCoachingDocId } : { name: null, id: null }),
        participantId: action.screen.participantId ?? (action.screen.coachingId === state.screen?.coachingId ? state.screen.participantId : null),
        source: action.screen.source !== undefined ? action.screen.source : state.screen?.source ?? null,
      }
      const next = {
        ...state,
        screen,
        history: [],
        selectedCoachingId: screen.coachingId ?? state.selectedCoachingId,
        ...(screen.kind === 'member' && { selectedMemberUserId: screen.memberId }),
        ...(screen.kind === 'massnahme' && { selectedMassnahmeId: screen.massnahmeId }),
        ...(screen.kind === 'modul' && { selectedMassnahmeId: screen.massnahmeId, selectedModulId: screen.modulId }),
        ...(action.screen.document && { selectedCoachingDocName: screen.document.name, selectedCoachingDocId: screen.document.id }),
      }
      return action.remember ? rememberScreen(state, next) : next
    }
    case 'SET_PERSON':
      return state.screen?.kind === 'personal' && state.screen.coachingId === action.coachingId
        ? { ...state, screen: { ...state.screen, participantId: action.participantId } } : state
    case 'CHANGE_TAB':
      return state.screen && state.screen.tab !== null ? { ...state, screen: { ...state.screen, tab: action.tab } } : state
    case 'CHANGE_PHASE': {
      if (!state.screen || state.screen.tab === null) return state
      const screen = { ...state.screen, kind: action.target.modal, tab: action.target.tab, document: action.target.document ?? state.screen.document, returnTo: action.target.returnTo }
      const next = {
        ...state,
        screen,
        selectedCoachingId: screen.coachingId ?? state.selectedCoachingId,
        ...(action.target.document && { selectedCoachingDocName: screen.document.name, selectedCoachingDocId: screen.document.id }),
      }
      return action.remember ? rememberScreen(state, next) : next
    }
    case 'GO_BACK': {
      if (returnsToDocuments(state.screen)) return dismissedState(state)
      const previous = state.history.at(-1)
      return previous ? { ...state, ...previous, history: state.history.slice(0, -1) } : state
    }
    case 'SET_PENDING_ERSTGESPRAECH':
      return { ...state, pendingErstgespraech: action.pending }
    case 'REOPEN_ERSTGESPRAECH':
      return state.screen ? state : {
        ...state,
        screen: { kind: 'erstgespraech', tab: 'checkliste', coachingId: action.coachingId, participantId: null, source: action.source, document: { name: null, id: null } },
        pendingErstgespraech: action.pending,
        history: [],
      }
    case 'TB_GENERATING': {
      const rest = state.tbGenerating.filter((id) => id !== action.coachingId)
      return { ...state, tbGenerating: action.generating ? [...rest, action.coachingId] : rest }
    }
    case 'CLOSE':
      return closedState(state)
    case 'DISMISS':
      return dismissedState(state)
    case 'CLOSE_FOR': {
      const screen = state.screen
      const same = screen && (screen.kind === 'erstgespraech'
        ? screen.coachingId === action.coachingId || (screen.coachingId === null && state.pendingErstgespraech?.coachingId === action.coachingId)
        : ['vertrag', 'ecn', 'verbis-lebenslauf', 'kompetenzfeststellung', 'kompetenzbilanzierung'].includes(screen.kind) && screen.coachingId === action.coachingId)
      return same ? closedState(state) : state
    }
    case 'BUMP_DASHBOARD':
      return { ...state, dashboardVersion: state.dashboardVersion + 1 }
    case 'SELECT_COACHING_DOC': {
      const document = { name: action.name, id: action.id ?? null }
      return {
        ...state,
        selectedCoachingDocName: document.name,
        selectedCoachingDocId: document.id,
        screen: state.screen && state.screen.coachingId === state.selectedCoachingId ? { ...state.screen, document } : state.screen,
      }
    }
    case 'SET_SETTINGS':
      return { ...state, settings: action.settings,
        orgSettingsTab: action.settings === 'organisation' ? 'organisation' : state.orgSettingsTab }
    case 'SET_VIEW':
      return state.settings ? { ...state, view: action.view, settings: null } : {
        ...closedState(state),
        view: action.view,
        selectedMemberUserId: null,
        selectedMassnahmeId: null,
        selectedModulId: null,
        selectedCoachingDocName: null,
        selectedCoachingDocId: null,
      }
    case 'SET_COACHING_VIEW_FILTER':
      return { ...state, coachingViewFilter: { ...state.coachingViewFilter, ...action.filter } }
    case 'BUMP_MEMBERS':
      return { ...state, membersVersion: state.membersVersion + 1 }
    case 'SET_MEMBER_VIEW_FILTER':
      return { ...state, memberViewFilter: { ...state.memberViewFilter, ...action.filter } }
    case 'BUMP_MASSNAHMEN':
      return { ...state, massnahmenVersion: state.massnahmenVersion + 1 }
    case 'SET_MASSNAHME_VIEW_FILTER':
      return { ...state, massnahmeViewFilter: { ...state.massnahmeViewFilter, ...action.filter } }
    default:
      return state
  }
}

function historyFrame(state: AppState): HistoryFrame | null {
  if (!state.screen || state.screen.tab === null) return null
  return { screen: state.screen as PhaseScreen, selectedCoachingId: state.selectedCoachingId,
    selectedCoachingDocName: state.selectedCoachingDocName, selectedCoachingDocId: state.selectedCoachingDocId }
}

function rememberScreen(state: AppState, next: AppState): AppState {
  const screen = next.screen
  if (!screen || screen.tab === null) return next
  if (screen.coachingId && screen.kind !== 'dokument') {
    const matches = (old: Screen) => old.kind === screen.kind && (screen.kind === 'personal' && screen.participantId !== null && old.participantId !== null
      ? old.participantId === screen.participantId : old.coachingId === screen.coachingId)
    const index = state.history.findIndex((frame) => matches(frame.screen))
    if (index >= 0) return { ...next, ...state.history[index],
      screen: { ...screen, source: state.history[index].screen.source }, selectedCoachingId: next.selectedCoachingId, history: state.history.slice(0, index) }
    if (state.screen && matches(state.screen)) return { ...next, screen: { ...screen, source: state.screen.source ?? null }, history: state.history }
  }
  const previous = historyFrame(state)
  return { ...next, history: previous ? [...state.history, previous] : [] }
}

function returnsToDocuments(screen: Screen | null): boolean {
  return screen?.kind === 'dokument' && screen.returnTo === 'documents' || screen?.kind === 'dokument-hochladen'
}

function dismissedState(state: AppState): AppState {
  return returnsToDocuments(state.screen)
    ? { ...state, screen: { ...state.screen, kind: 'personal', tab: 'dokumente', returnTo: undefined } as PhaseScreen }
    : closedState(state)
}

function closedState(state: AppState): AppState {
  return { ...state,
    ...(state.screen?.coachingId && { selectedCoachingId: null, selectedCoachingDocName: null, selectedCoachingDocId: null }),
    screen: null, pendingErstgespraech: null, history: [] }
}

type AppContextValue = {
  identity: Me | null
  members: Member[]
  state: AppState
  dispatch: Dispatch<Action>
  coachings: DashboardCoaching[]
  coachingsLoading: boolean
  zurueckgezogen: ZurueckgezogenCoaching[]
  reloadDashboard: () => void
  optimisticDashboard: (edit: RowsEdit<DashboardCoaching>) => Pending
  removedRows: ReadonlySet<string>
  optimisticRemove: (key: string) => Pending
  reloadMembers: () => void
  reloadMassnahmen: () => void
}

const AppContext = createContext<AppContextValue | null>(null)

function toastBoardFailed(error: unknown): void {
  toastError(`Board konnte nicht aktualisiert werden: ${reasonOf(error)}`)
}

function reasonOf(error: unknown): string {
  if (error instanceof ApiError) return error.detailText || `HTTP ${error.status}`
  if (error instanceof Error) return error.message
  return String(error)
}

function mergeCard(
  rows: DashboardCoaching[],
  id: string,
  card: DashboardCoaching | null,
): DashboardCoaching[] {
  if (!card) return rows.filter((row) => row.id !== id)
  const at = rows.findIndex((row) => row.id === id)
  if (at === -1) return [...rows, card]
  const next = rows.slice()
  next[at] = card
  return next
}

type BoardRead = {
  sequence: number
  promise: Promise<string | void>
  cards: Map<string, DashboardCoaching | null>
}
type BoardEdit = { edit: RowsEdit<DashboardCoaching>; confirmedAt: number | null }

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reduceStaff, initialStaffState)
  const [identity, setIdentity] = useState<Me | null>(null)
  useEffect(() => {
    let cancelled = false
    const unsubscribe = subscribeMe((value) => { if (!cancelled) setIdentity(value) })
    me().then((value) => { if (!cancelled) setIdentity(value) })
      .catch((error) => console.error('Identität konnte nicht geladen werden:', error))
    return () => { cancelled = true; unsubscribe() }
  }, [])
  const [members, setMembers] = useState<Member[]>([])
  useEffect(() => {
    let cancelled = false
    listMembers().then((rows) => { if (!cancelled) setMembers(rows) })
      .catch((error) => console.error('Mitglieder laden fehlgeschlagen:', error))
    return () => { cancelled = true }
  }, [state.membersVersion])
  const [serverCoachings, setCoachings] = useState<DashboardCoaching[]>([])
  const [coachingsLoading, setCoachingsLoading] = useState(true)
  const [edits, setEdits] = useState<BoardEdit[]>([])
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const boardRead = useRef<BoardRead>({ sequence: 0, promise: Promise.resolve(), cards: new Map() })
  const loadBoard = useCallback((): Promise<string | void> => {
    const identity = captureWriteIdentity()
    const read: BoardRead = { sequence: boardRead.current.sequence + 1, promise: Promise.resolve(), cards: new Map() }
    let cursor: string | undefined
    const load: Promise<string | void> = listDashboard((value) => { cursor = value }).then(
      (rows) => {
        if (read !== boardRead.current) return boardRead.current.promise
        if (!mounted.current || !isWriteIdentityCurrent(identity)) return
        let landed = rows
        for (const [id, card] of read.cards) landed = mergeCard(landed, id, card)
        setCoachings(landed)
        setCoachingsLoading(false)
        setEdits((previous) => {
          const kept = previous.filter((edit) => edit.confirmedAt === null || read.sequence <= edit.confirmedAt)
          return kept.length === previous.length ? previous : kept
        })
        return cursor
      },
      (cause: unknown) => {
        if (read !== boardRead.current) return boardRead.current.promise
        if (mounted.current && isWriteIdentityCurrent(identity)) setCoachingsLoading(false)
        throw cause
      },
    )
    read.promise = load
    boardRead.current = read
    load.catch((cause: unknown) => {
      if (mounted.current && read === boardRead.current && isWriteIdentityCurrent(identity)) toastBoardFailed(cause)
    })
    return load
  }, [])
  useEffect(() => {
    void loadBoard()
  }, [state.dashboardVersion, loadBoard])
  const latestCardLoad = useRef(new Map<string, number>())
  const onLiveHint = useCallback((hint: LiveHint): Promise<void> => {
    const identity = captureWriteIdentity()
    const id = hint.coachingId
    const mine = (latestCardLoad.current.get(id) ?? 0) + 1
    latestCardLoad.current.set(id, mine)
    const land = (card: DashboardCoaching | null) => {
      boardRead.current.cards.set(id, card)
      setCoachings((rows) => mergeCard(rows, id, card))
    }
    if (hint.kind === 'gone') {
      land(null)
      return Promise.resolve()
    }
    const sinceLoad = boardRead.current
    return loadDashboardCoaching(id).then(
      (card) => {
        if (!mounted.current || latestCardLoad.current.get(id) !== mine || !isWriteIdentityCurrent(identity)) return
        if (sinceLoad !== boardRead.current) return
        land(card)
      },
      (cause: unknown) => {
        if (mounted.current && isWriteIdentityCurrent(identity)) toastBoardFailed(cause)
        throw cause
      },
    )
  }, [])
  const { refresh: refreshSession } = useAuth()
  const onUnauthorized = useCallback(() => {
    const identity = captureWriteIdentity()
    refreshSession().catch((cause: unknown) => {
      if (!mounted.current || !isWriteIdentityCurrent(identity)) return
      toastError(`Anmeldung konnte nicht geprüft werden: ${reasonOf(cause)}`)
    })
  }, [refreshSession])
  useEffect(() => startLiveConnection({
    snapshot: boardRead.current.promise,
    onHint: onLiveHint,
    catchUp: loadBoard,
    onUnauthorized,
  }), [onLiveHint, loadBoard, onUnauthorized])
  const optimisticDashboard = useCallback((edit: RowsEdit<DashboardCoaching>): Pending => {
    const entry: BoardEdit = { edit, confirmedAt: null }
    let settled = false
    setEdits((previous) => [...previous, entry])
    return {
      confirm: () => {
        if (!settled) {
          settled = true
          const confirmedAt = boardRead.current.sequence
          setEdits((previous) => previous.map((item) => item === entry ? { ...entry, confirmedAt } : item))
        }
        dispatch({ type: 'BUMP_DASHBOARD' })
      },
      rollback: () => {
        if (settled) return
        settled = true
        setEdits((previous) => previous.filter((item) => item !== entry))
      },
    }
  }, [])
  const [removedRows, setRemovedRows] = useState<ReadonlySet<string>>(() => new Set())
  const optimisticRemove = useCallback((key: string): Pending => {
    setRemovedRows((s) => new Set(s).add(key))
    return {
      confirm: () => {},
      rollback: () =>
        setRemovedRows((s) => {
          const next = new Set(s)
          next.delete(key)
          return next
        }),
    }
  }, [])
  const rows = useMemo(() => edits.reduce((current, item) => item.edit(current), serverCoachings), [serverCoachings, edits])
  const coachings = useMemo(() => rows.filter((c) => !hatRueckzug(c)), [rows])
  const zurueckgezogen = useMemo(() => rows.filter(hatRueckzug), [rows])
  const value = useMemo<AppContextValue>(
    () => ({
      state,
      dispatch,
      identity,
      members: removedRows.size ? members.filter((member) => !removedRows.has(optimisticRemoveKey('member', member.userId))) : members,
      coachings,
      coachingsLoading,
      zurueckgezogen,
      reloadDashboard: () => dispatch({ type: 'BUMP_DASHBOARD' }),
      optimisticDashboard,
      removedRows,
      optimisticRemove,
      reloadMembers: () => dispatch({ type: 'BUMP_MEMBERS' }),
      reloadMassnahmen: () => dispatch({ type: 'BUMP_MASSNAHMEN' }),
    }),
    [state, identity, members, coachings, coachingsLoading, zurueckgezogen, optimisticDashboard, removedRows, optimisticRemove],
  )
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used within AppProvider')
  return ctx
}

export function optimisticRemoveKey(
  list: 'member' | 'massnahme' | 'modul' | 'modul-link',
  ...ids: string[]
): string {
  return `${list}:${ids.join(':')}`
}

export function useWithoutRemoved<T>(
  rows: T[],
  keyOf: (row: T) => string,
): T[] {
  const { removedRows } = useApp()
  return useMemo(
    () => (removedRows.size === 0 ? rows : rows.filter((r) => !removedRows.has(keyOf(r)))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, removedRows],
  )
}

export function useZurueckgezogenRow(coachingId: string | null): ZurueckgezogenCoaching | null {
  const { zurueckgezogen } = useApp()
  return zurueckgezogen.find((c) => c.id === coachingId) ?? null
}

export function useCoachingRow(coachingId: string | null): DashboardCoaching | null {
  const { coachings, zurueckgezogen } = useApp()
  return coachings.find((c) => c.id === coachingId) ?? zurueckgezogen.find((c) => c.id === coachingId) ?? null
}
